/**
 * Gobodo Record Service
 *
 * Service layer for CRUD operations on record instances using Gobodo API.
 * Handles conversion between our GenericRecord format and Gobodo's GraphObjectPayload/GraphObjectResponse.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig, { getCustomSuffix } from '../../config/gobodoConfig';
import type { GenericRecord } from '../recordStorageService';
import { getAllGobodoFields } from './gobodoObjectService';
import { handleGobodoError, logGobodoError, ensureError } from '../../utils/gobodoErrorHandler';
import { callWithHttpInfo } from '../../utils/gobodoCallWithHttpInfo';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { normalizeToGobodoFormat } from '../../utils/objectApiNameNormalizer';
import type { CustomField } from '../queries/fieldQueries';
import { serializeGobodoData } from '../providers/gobodoRoleProvider';
// Re-export for backward compatibility (recordStorageService, gobodoRecordQueries). Prefer `./gobodoCacheConfig` in new code.
import {
  makeObjectByIdReadCacheOpts,
  makeObjectByKeyReadCacheOpts,
  makeObjectPropertyWriteCacheOpts,
  type GobodoCacheOpts,
} from './gobodoCacheConfig';
import {
  ensureGobodoCustomSuffixOnRecordKey,
  isGobodoGraphObjectKeyPlainUuid,
} from '../../utils/gobodoRecordKeySuffix';
import { getPAWCSAuditRelKey } from '../../config/gobodoAccountRelationshipKeys';
export type { GobodoCacheOpts };

const GOBODO_RECORD_DEBUG =
  typeof window !== 'undefined' && (window as any).__GOBODO_DEBUG_RECORDS__ === true;

/** Short-lived cache so single-record creates and other callers do not re-fetch all field pages per row. */
const PAYLOAD_FIELD_DEFS_CACHE_TTL_MS = 120_000;
const payloadFieldDefsCache = new Map<string, { expires: number; fields: CustomField[] }>();

function payloadFieldDefsCacheKey(objectApiName: string, typeId: number): string {
  return `${objectApiName}\0${typeId}`;
}

const LOOKUP_DISPLAY_KEYS: Record<string, string> = {
  OwnerId__c: 'Owner__c',
  AccountId__c: 'AccountName__c',
  ContactId__c: 'ContactName__c',
  ProductId__c: 'ProductName__c',
  Product__c: 'ProductName__c',
  // Relationship skin fields (don't end with "Id" so need explicit mapping)
  ClientRelField__c: 'ClientName__c',
  CSAuditRelField__c: 'CSAuditName__c',
  AccountOwnerRelField__c: 'AccountOwnerName__c',
  CreatedByRelField__c: 'CreatedByName__c',
  LastModifiedByRelField__c: 'LastModifiedByName__c',
  ReferralCompanyRelField__c: 'ReferralCompanyName__c',
  ReferralPersonAccountRelField__c: 'ReferralPersonAccountName__c',
  AccountManagerCompanyRelField__c: 'AccountManagerCompanyName__c',
  ReferralPartnerContactRelField__c: 'ReferralPartnerContactName__c',
  AccountManagerRelField__c: 'AccountManagerName__c',
  DayToDayContactRelField__c: 'DayToDayContactName__c',
  // Relationship-typed fields that don't follow the *RelField naming convention
  // (typed as 'relationship' in Gobodo but named like text fields). Read converter needs
  // to know these expose a related object so the label can be surfaced.
  Spouse__c: 'SpouseName__c',
  Primary_Parent__c: 'Primary_Parent_Name__c',
};

export function getLookupDisplayKey(idFieldKey: string): string | null {
  if (LOOKUP_DISPLAY_KEYS[idFieldKey]) return LOOKUP_DISPLAY_KEYS[idFieldKey];
  // Normalize to __c and check again (handles __stg3_c, __stg5_c, __stg_c, __dev_c variants)
  const normalized = idFieldKey.replace(/(__stg3_c|__stg5_c|__stg_c|__dev_c|_stg3_c)$/i, '__c');
  if (normalized !== idFieldKey && LOOKUP_DISPLAY_KEYS[normalized]) return LOOKUP_DISPLAY_KEYS[normalized];
  const base = idFieldKey.replace(/(_stg3_c|__stg3_c|__stg5_c|__stg_c|__dev_c|__c)$/i, '');
  if (base.endsWith('Id')) return base.slice(0, -2) + '__c';
  if (base.endsWith('RelField')) return base.replace(/RelField$/, 'Name__c');
  return null;
}

/**
 * True when `label` is suitable to treat as a human display name (vs Gobodo placeholders like "Client-123").
 * Aligns with CyberSweep list heuristics so we do not copy synthetic labels into Name__c.
 */
function isUsableGobodoDisplayLabel(label: string): boolean {
  const t = label.trim();
  if (!t) return false;
  if (/^(CyberAudit|CSAudit|PAWRecord|Client)-\d+$/i.test(t)) return false;
  if (/^a\d[a-z]\d/i.test(t)) return false;
  if (/^Record\s+/i.test(t)) return false;
  return true;
}

function isPawRecordObjectType(objectApiName: string): boolean {
  return normalizeToGobodoFormat(objectApiName).replace(/(__stg3_c|__stg5_c|__stg_c|__dev_c|__pp2_c|__pp_c|__c)$/i, '').toLowerCase() === 'pawrecord';
}

function getPAWRecordCSAuditRef(recordData: Record<string, any>): string {
  const raw =
    recordData.parentobjectid ??
    recordData.parentobjectkey ??
    recordData.CS_Audit__c ??
    recordData.custom__cs_audit ??
    recordData.csAuditId ??
    recordData.CSAuditRelField__c;
  return String(raw ?? '').trim();
}

function ensureConfiguredGobodoKeySuffix(key: string): string {
  const s = String(key ?? '').trim();
  if (!s) return s;
  if (/^[0-9]+$/.test(s)) return s;
  if (isGobodoGraphObjectKeyPlainUuid(s)) return s;
  if (/(__c|__stg_c|__stg3_c|__stg5_c|__dev_c|__mj_c|__pp2_c|__pp_c)$/i.test(s)) return s;
  return `${s}${getCustomSuffix()}`;
}

type PAWRecordParentLink =
  | { parentobjectid: number; parentobjectkey?: undefined }
  | { parentobjectid?: undefined; parentobjectkey: string }
  | { parentobjectid?: undefined; parentobjectkey?: undefined };

async function resolvePAWRecordParentLink(recordData: Record<string, any>): Promise<PAWRecordParentLink> {
  const rawRef = getPAWRecordCSAuditRef(recordData);
  if (!rawRef) return {};
  if (!/^\d+$/.test(rawRef)) {
    return { parentobjectkey: ensureConfiguredGobodoKeySuffix(rawRef) };
  }

  return { parentobjectid: Number(rawRef) };
}

// ============================================
// TYPE CONVERSION
// ============================================

/**
 * Safely convert date value to ISO string
 * Handles Date objects, strings, timestamps (numbers), and null/undefined
 */
function toISOString(value: any): string {
  if (!value) {
    return new Date().toISOString();
  }

  // Already a Date object
  if (value instanceof Date) {
    return value.toISOString();
  }

  // String that might be ISO format or timestamp
  if (typeof value === 'string') {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }

  // Number timestamp (milliseconds or seconds)
  if (typeof value === 'number') {
    // If timestamp is in seconds (less than year 2000 in milliseconds), convert to milliseconds
    const timestamp = value < 946684800000 ? value * 1000 : value;
    const parsed = new Date(timestamp);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }

  // Fallback to current date
  return new Date().toISOString();
}

/**
 * Convert our GenericRecord format to Gobodo GraphObjectPayload
 * @param objectApiName - Object type API name (e.g., "Product__c")
 * @param recordData - Record data with field values
 * @param objectTypeId - Optional: Object type ID from Gobodo (will be fetched if not provided)
 * @param preloadedFieldDefinitions - Optional: field list from {@link getAllGobodoFields} (avoids per-row /objecttype and /fields calls during batch creates)
 * @returns GraphObjectPayload ready for API call
 */
async function convertToGobodoRecordPayload(
  objectApiName: string,
  recordData: Record<string, any>,
  objectTypeId?: number,
  preloadedFieldDefinitions?: CustomField[]
): Promise<GobodoApi.GraphObjectPayload> {
  // Get object type ID if not provided
  let typeId = objectTypeId;
  if (!typeId) {
    // Get object type directly from API to get numeric ID.
    // Using *WithHttpInfo so request IDs are logged for traceability — this
    // call is a prerequisite for every write and its failures are hard to
    // diagnose without a backend trace ID.
    const client = getGobodoClient();
    const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
    const objectType = await retryWithBackoff(
      () =>
        callWithHttpInfo<{ id?: number; key?: string }>(
          () => objectTypesApi.getObjectTypeByKeyWithHttpInfo(objectApiName, { maxFields: 0 }),
          { operation: `getObjectTypeByKey(${objectApiName})` }
        ),
      { maxRetries: 3 }
    );
    typeId = objectType.id as any;
  }

  // Extract label from record data
  const label = extractRecordLabel(recordData);

  // Field definitions: use preload (batch create), then TTL cache, then one full paginated fetch
  let fieldDefinitions: CustomField[] = [];
  try {
    if (preloadedFieldDefinitions && preloadedFieldDefinitions.length > 0) {
      fieldDefinitions = preloadedFieldDefinitions;
    } else {
      const now = Date.now();
      const ck = payloadFieldDefsCacheKey(objectApiName, typeId as number);
      const cached = payloadFieldDefsCache.get(ck);
      if (cached && cached.expires > now) {
        fieldDefinitions = cached.fields;
      } else {
        fieldDefinitions = await getAllGobodoFields(objectApiName, { objectTypeId: typeId as number });
        payloadFieldDefsCache.set(ck, { expires: now + PAYLOAD_FIELD_DEFS_CACHE_TTL_MS, fields: fieldDefinitions });
      }
    }
  } catch (error) {
    console.warn(`[convertToGobodoRecordPayload] Could not fetch field definitions for ${objectApiName}:`, error);
    // Continue without field definitions - will treat all values as-is
  }

  // Create a map of field API names to field types for quick lookup
  const fieldTypeMap: Record<string, string> = {};
  fieldDefinitions.forEach(field => {
    fieldTypeMap[field.fieldName] = field.fieldType;
  });

  // Create GraphObjectPayload
  const payload = new GobodoApi.GraphObjectPayload();
  payload.typekey = objectApiName;
  payload.typeid = typeId as any;
  payload.label = label;
  payload.graphs = [GobodoConfig.graph()];
  if (isPawRecordObjectType(objectApiName)) {
    const parentLink = await resolvePAWRecordParentLink(recordData);
    if (parentLink.parentobjectkey || parentLink.parentobjectid != null) {
      if (parentLink.parentobjectkey) {
        (payload as GobodoApi.GraphObjectPayload & { parentobjectkey?: string }).parentobjectkey = parentLink.parentobjectkey;
      }
      if (parentLink.parentobjectid != null) {
        (payload as GobodoApi.GraphObjectPayload & { parentobjectid?: number }).parentobjectid = parentLink.parentobjectid;
      }
      (payload as GobodoApi.GraphObjectPayload & { parentobjectkey?: string; relationshiptypekey?: string }).relationshiptypekey = getPAWCSAuditRelKey();
    }
  }
  
  // Put all field values in properties object
  // Exclude metadata/system fields that Gobodo handles separately or that leak from record conversion
  const metadataFields = ['id', 'name', 'label', 'createdDate', 'lastModified', 'createdBy', 'lastModifiedBy'];
  const properties: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(recordData)) {
    if (!metadataFields.includes(key) && value !== undefined && value !== null) {
      const fieldType = fieldTypeMap[key];
      const isNumericField = fieldType === 'number' || fieldType === 'currency' || fieldType === 'percent';
      const isLookupField = fieldType === 'lookup' || fieldType === 'masterdetail';
      const isPicklistField = fieldType === 'picklist';
      const isMultipicklistField = fieldType === 'multipicklist';
      
      if (isNumericField && typeof value === 'string') {
        // Convert string to number for numeric fields
        const numValue = Number(value);
        if (!isNaN(numValue)) {
          properties[key] = numValue;
        } else {
          // If conversion fails, keep original value
          properties[key] = value;
        }
      } else if (isLookupField && typeof value === 'string') {
        // For lookup/masterdetail fields, preserve graph keys as strings and only coerce
        // fully numeric ids to numbers for Gobodo object-id references.
        const numericId = extractGobodoId(value);
        if (!isNaN(numericId)) {
          properties[key] = numericId;
          if (GOBODO_RECORD_DEBUG) console.log(`[convertToGobodoRecordPayload] Lookup field ${key}: converted "${value}" to numeric ID ${numericId}`);
        } else {
          properties[key] = value;
        }
      } else if (isPicklistField) {
        // For single-select picklist fields, ensure value is a string (not an array)
        // If value is an array, take the first element
        if (Array.isArray(value)) {
          properties[key] = value.length > 0 ? String(value[0]) : '';
          if (GOBODO_RECORD_DEBUG) console.log(`[convertToGobodoRecordPayload] Picklist field ${key}: converted array to single value "${properties[key]}"`);
        } else {
          properties[key] = String(value);
        }
      } else if (isMultipicklistField) {
        // For multiple-select picklist fields, ensure value is an array
        if (Array.isArray(value)) {
          // Ensure all array elements are strings
          properties[key] = value.map(v => String(v)).filter(v => v !== '');
        } else if (value !== null && value !== undefined && value !== '') {
          // If value is a single string, convert to array
          properties[key] = [String(value)];
          if (GOBODO_RECORD_DEBUG) console.log(`[convertToGobodoRecordPayload] Multipicklist field ${key}: converted single value to array ["${properties[key][0]}"]`);
        } else {
          // Empty value should be empty array
          properties[key] = [];
        }
      } else {
        // For non-numeric, non-lookup, non-picklist fields, keep value as-is
        properties[key] = value;
      }
    }
  }

  if (label && (properties.name === undefined || properties.name === null || properties.name === '')) {
    properties.name = label;
  }

  // Name__c is required on most Gobodo object types — ensure it's set from label
  if (!properties.Name__c && label) {
    properties.Name__c = label;
  }

  payload.properties = properties;

  return payload;
}

/**
 * Convert Gobodo GraphObjectResponse to our GenericRecord format
 * @param gobodoRecord - GraphObjectResponse from Gobodo API
 * @param objectApiName - Object type API name for ID formatting
 * @returns GenericRecord in our format
 */
export function convertFromGobodoRecord(
  gobodoRecord: any,
  objectApiName: string
): GenericRecord {
  if (GOBODO_RECORD_DEBUG) console.log(`[convertFromGobodoRecord] Converting Gobodo record:`, gobodoRecord);
  let fieldData: Record<string, any> = gobodoRecord.data || gobodoRecord.properties || {};
  if (Object.keys(fieldData).length === 0 && gobodoRecord && typeof gobodoRecord === 'object') {
    const meta = new Set(['id', 'key', 'creationdate', 'creationDate', 'modificationdate', 'modificationDate', 'creatorid', 'objecttype', 'objecttypekey', 'label', 'content', 'items', 'data', 'properties']);
    fieldData = Object.fromEntries(
      Object.entries(gobodoRecord).filter(([k]) => !meta.has(k.toLowerCase()))
    );
  }
  if (GOBODO_RECORD_DEBUG) console.log(`[convertFromGobodoRecord] Extracted fieldData:`, fieldData);
  
  // Convert Gobodo ID to string format
  const recordId = String(gobodoRecord.id);
  
  // Process field data: extract lookup field values from relationship objects
  // Preserve arrays for multipicklist fields (Gobodo returns arrays for multiple-select fields)
  const processedFieldData: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(fieldData)) {
    // Check if this is a lookup field (relationship object)
    // Lookup fields come back as objects with a "relatedObject" property
    if (value && typeof value === 'object' && !Array.isArray(value) && 'relatedObject' in value) {
      const relationship = value as any;
      const ro = relationship.relatedObject;
      if (ro && ro.id !== undefined) {
        processedFieldData[key] = String(ro.key ?? ro.id);
        const displayLabel = (ro.label ?? ro.name ?? ro.key ?? '').toString().trim();
        if (displayLabel) {
          const displayKey = getLookupDisplayKey(key);
          if (displayKey) processedFieldData[displayKey] = displayLabel;
        }
        if (GOBODO_RECORD_DEBUG) console.log(`[convertFromGobodoRecord] Lookup field ${key}: id=${ro.id}, display=${displayLabel}`);
      } else {
        processedFieldData[key] = value;
        if (!ro) console.warn(`[convertFromGobodoRecord] Lookup field ${key} has relationship object but missing relatedObject.id`);
      }
    } else if (Array.isArray(value)) {
      // Preserve arrays as-is - these are likely multipicklist fields from Gobodo
      // Ensure all array elements are strings for consistency
      processedFieldData[key] = value.map(v => {
        if (v === null || v === undefined) return '';
        return String(v);
      }).filter(v => v !== '');
      if (GOBODO_RECORD_DEBUG) console.log(`[convertFromGobodoRecord] Preserved array for field ${key}:`, processedFieldData[key]);
    } else {
      processedFieldData[key] = serializeGobodoData(value);
    }
  }
  
  // Build record with all field values
  // Extract createdBy - check name fields first, then fall back to ID/metadata
  const createdByValue = processedFieldData.CreatedBy__c           // SF-style name field (saved by app)
    || processedFieldData.createdBy                                // camelCase name field
    || processedFieldData.CreatedByName__c                         // SF relationship name field
    || gobodoRecord.creatorid                                      // Gobodo metadata
    || processedFieldData.CreatedById__c                           // ID field (fallback)
    || processedFieldData.CreatedById
    || processedFieldData.createdById
    || gobodoRecord.createdBy
    || '';
  // Extract lastModifiedBy - check name fields FIRST, then fall back to ID field
  // When we save, we store the name in 'LastModifiedBy__c' and ID in 'LastModifiedById__c'
  const lastModifiedByValue = processedFieldData.LastModifiedBy__c // SF-style name field (saved by app)
    || processedFieldData.lastModifiedBy                           // camelCase name field
    || processedFieldData.LastModifiedByName__c                    // SF relationship name field
    || processedFieldData.LastModifiedById__c                      // ID field (fallback)
    || processedFieldData.LastModifiedById
    || processedFieldData.lastModifiedById
    || processedFieldData.last_modified_by
    || '';
  const record: GenericRecord = {
    id: recordId,
    ...processedFieldData,
    // Map metadata fields with safe date conversion
    createdDate: toISOString(gobodoRecord.creationdate || gobodoRecord.creationDate),
    lastModified: toISOString(gobodoRecord.modificationdate || gobodoRecord.modificationDate),
    createdBy: createdByValue ? String(createdByValue) : 'system',
    // Map lastModifiedBy from field data - prioritizes name field, falls back to ID
    lastModifiedBy: lastModifiedByValue ? String(lastModifiedByValue) : '',
    // Preserve label if present
    label: gobodoRecord.label || processedFieldData.label || processedFieldData.name || '',
  };
  if (gobodoRecord.key != null) {
    (record as Record<string, unknown>).key = String(gobodoRecord.key);
  }

  const customSuffix = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
  const accountRelKey = `AccountRelField${customSuffix}`;
  const objNorm = (objectApiName || '').toLowerCase().replace(/__stg3_c$|__stg_c$|__c$/i, '');
  if (objNorm === 'contact' || objNorm === 'client' || objNorm === 'case') {
    const rawData = gobodoRecord.data || gobodoRecord.properties || gobodoRecord;
    let accountRel =
      record[accountRelKey] ?? record.AccountRelField__c ?? record.AccountId__c ?? record.accountId ??
      (rawData && typeof rawData === 'object' ? (rawData as Record<string, unknown>).AccountId__c : undefined);
    if (accountRel != null && accountRel !== '') {
      if (typeof accountRel === 'object' && accountRel !== null && 'relatedObject' in accountRel) {
        const ro = (accountRel as { relatedObject?: { key?: unknown; id?: unknown } }).relatedObject;
        accountRel = ro?.key ?? ro?.id ?? accountRel;
      }
      record.accountId = String(accountRel);
    }
  }

  // Traversal APIs often return GraphObjectResponse with a populated `label` but sparse `data`; list UIs expect Name__c / ClientName__c.
  if (objNorm === 'client') {
    const labelStr = String(record.label ?? '').trim();
    const hasNameField =
      (record.Name__c != null && String(record.Name__c).trim() !== '') ||
      (record.ClientName__c != null && String(record.ClientName__c).trim() !== '') ||
      (record['Name'] != null && String(record['Name']).trim() !== '');
    if (!hasNameField && labelStr && isUsableGobodoDisplayLabel(labelStr)) {
      (record as Record<string, unknown>).Name__c = labelStr;
      (record as Record<string, unknown>).ClientName__c = labelStr;
    }
  }

  // Map FirmRelField__c / PartnerRelField__c (relationship skin fields) to accountId / partnerId for Pricebook__c
  const firmRelKey = `FirmRelField${customSuffix}`;
  const partnerRelKey = `PartnerRelField${customSuffix}`;
  if (objNorm === 'pricebook') {
    const firmVal = record[firmRelKey] ?? record.accountId;
    if (firmVal != null && firmVal !== '') {
      record.accountId = String(firmVal);
    }
    const partnerVal = record[partnerRelKey] ?? record.partnerId;
    if (partnerVal != null && partnerVal !== '') {
      record.partnerId = String(partnerVal);
    }
  }

  // Map ClientRelField (relationship skin field) to relatedClientId for CreditBlock, Credit_Bureaus, Unlock_Request, CSAudit, PAWRecord
  if (objNorm.startsWith('creditblock') || objNorm.includes('credit_bureaus') || objNorm.includes('unlock_request') ||
      objNorm === 'csaudit' || objNorm === 'pawrecord' || objNorm === 'cybersweep' || objNorm.includes('cybersweep')) {
    const clientRelKey = `ClientRelField${customSuffix}`;
    const clientVal =
      record[clientRelKey] ?? record.ClientRelField__c ?? record.Related_Account__c ?? record.relatedClientId ??
      record.ClientId__c ?? record.Account__c ?? record.Client__c ?? record.custom__client ??
      record.Credit_Block_Client__c ?? record.custom__credit_block_client ?? record.custom__account;
    if (clientVal != null && clientVal !== '') {
      (record as Record<string, unknown>).relatedClientId = String(clientVal);
    }
    // Also expose the client display name as clientName
    // RDS sync stores this as ClientName__c (flat string); lookup resolution stores as ClientName__c too
    const clientNameVal = record.ClientName__c ?? record[`ClientName${customSuffix}`] ?? record.AccountName__c;
    if (clientNameVal != null && clientNameVal !== '') {
      (record as Record<string, unknown>).clientName = String(clientNameVal);
    }
  }

  // Map CSAuditRelField / CS_Audit__c to csAuditId for PAWRecord
  if (objNorm === 'pawrecord') {
    const csAuditRelKey = `CSAuditRelField${customSuffix}`;
    const csAuditVal =
      record[csAuditRelKey] ?? record.CSAuditRelField__c ?? record.CS_Audit__c ?? record.csAuditId ?? record.custom__cs_audit;
    if (csAuditVal != null && csAuditVal !== '') {
      (record as Record<string, unknown>).csAuditId = String(csAuditVal);
    }
  }

  if (GOBODO_RECORD_DEBUG) console.log(`[convertFromGobodoRecord] Converted record:`, record);
  return record;
}

/**
 * Extract record label from record data
 * Priority: name, title, subject, label fields, then first text field (excluding ID/lookup fields)
 */
function extractRecordLabel(recordData: Record<string, any>): string {
  const nameFields = ['Name__c', 'name', 'title', 'subject', 'label', 'displayName', 'Title__c', 'Subject__c'];
  for (const field of nameFields) {
    const v = recordData[field];
    if (v != null && typeof v === 'string' && v.trim()) return v.trim();
  }

  // Fallback: first non-empty text field, but skip ID/lookup/metadata fields
  // These are fields that contain UUIDs or IDs, not display names
  const skipFields = [
    'id', 'createdDate', 'lastModified', 'createdBy', 'lastModifiedBy',
    // ID/lookup fields that contain UUIDs or numeric IDs
    'Account__c', 'custom__account', 'AccountId__c', 'accountId',
    'Client__c', 'custom__client', 'ClientId__c', 'clientId',
    'Contact__c', 'ContactId__c', 'contactId',
    'OwnerId__c', 'ownerId', 'Owner__c',
    'CreatedById__c', 'LastModifiedById__c',
    // Relationship fields
    'AccountRelField__c', 'ClientRelField__c', 'CSAuditRelField__c',
    // Template/Type fields (metadata, not display names)
    'Audit_Template__c', 'Audit_Type_Name__c', 'Template__c', 'Type__c',
    // Date fields
    'Phase_1_Due_Date__c', 'Phase_2_Due_Date__c', 'Due_Date__c',
    'Created_Date__c', 'Modified_Date__c', 'createdAt', 'updatedAt',
  ];

  for (const [key, value] of Object.entries(recordData)) {
    // Skip known metadata/ID fields
    if (skipFields.includes(key)) continue;
    // Skip fields ending with Id (case-insensitive) - likely lookup IDs
    if (/Id(__c)?$/i.test(key)) continue;
    // Skip fields ending with RelField - relationship fields
    if (/RelField(__c)?$/i.test(key)) continue;
    // Skip fields containing Template or Type - metadata, not display names
    if (/template|type/i.test(key)) continue;
    // Skip fields containing Date, Time, or ending with _at - date/time fields
    if (/date|time|_at$/i.test(key)) continue;
    // Skip UUID-like values (even if field name doesn't indicate it's an ID)
    if (typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) continue;
    // Skip ISO timestamp values (YYYY-MM-DDT...)
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) continue;

    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  // Final fallback - don't use generic timestamp, let Gobodo auto-generate
  return '';
}

/**
 * Extract Gobodo numeric ID from our record ID format
 * Handles both formats:
 * - Gobodo format: numeric string (e.g., "12345")
 * - Our format: "rec_objectname_timestamp_random" (extract numeric part if possible)
 */
function extractGobodoId(recordId: string): number {
  const raw = String(recordId ?? '').trim();
  if (!/^[0-9]+$/.test(raw)) return Number.NaN;
  return Number(raw);
}

const GOBODO_PAYLOAD_METADATA_FIELDS = [
  'id',
  'name',
  'label',
  'createdDate',
  'lastModified',
  'createdBy',
  'lastModifiedBy',
];

export type GobodoUpdateMode = 'fullObject' | 'propertyPatch';

export interface UpdateGobodoRecordOptions {
  /** Default `fullObject` (merge + updateObject). Use `propertyPatch` for PUT /object/{id}/properties only. */
  gobodoUpdateMode?: GobodoUpdateMode;
}

/**
 * Coerce one property for Gobodo payloads (merge or property PATCH).
 * Returns `undefined` to omit the key (null/undefined/metadata or unhandled nested object).
 */
export function coerceGobodoPropertyForPayload(
  key: string,
  value: unknown,
  fieldTypeMap: Record<string, string>
): unknown | undefined {
  if (GOBODO_PAYLOAD_METADATA_FIELDS.includes(key) || value === undefined || value === null) {
    return undefined;
  }
  const ft = fieldTypeMap[key];
  if ((ft === 'number' || ft === 'currency' || ft === 'percent') && typeof value === 'string') {
    const n = Number(value);
    return Number.isNaN(n) ? value : n;
  }
  if ((ft === 'lookup' || ft === 'masterdetail') && typeof value === 'string') {
    const n = extractGobodoId(value);
    return Number.isNaN(n) ? value : n;
  }
  if (value && typeof value === 'object' && !Array.isArray(value) && 'relatedObject' in (value as object)) {
    const relObj = value as { relatedObject?: { id?: unknown } };
    return relObj.relatedObject?.id ?? value;
  }
  if (ft === 'picklist') {
    return Array.isArray(value) ? (value[0] ?? '') : String(value);
  }
  if (ft === 'multipicklist') {
    return Array.isArray(value)
      ? (value as unknown[]).map(String).filter(Boolean)
      : value
        ? [String(value)]
        : [];
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const o = value as Record<string, unknown>;
    if ('string' in o && typeof o.string === 'string') return o.string;
    if ('value' in o && (typeof o.value === 'string' || typeof o.value === 'number')) return o.value;
    return undefined;
  }
  return value;
}

// ============================================
// RECORD OPERATIONS
// ============================================

/**
 * Create a new record instance in Gobodo
 * @param objectApiName - Object type API name (e.g., "Product__c")
 * @param recordData - Record data with field values
 * @returns Created record in GenericRecord format
 */
export async function createGobodoRecord(
  objectApiName: string,
  recordData: Record<string, any>
): Promise<GenericRecord> {
  console.group('[createGobodoRecord] === GOBODO API CREATE DEBUG ===');
  console.log('objectApiName:', objectApiName);
  console.log('recordData input:', JSON.stringify(recordData, null, 2));

  // Verify object type exists (auto-creates if missing)
  await ensureObjectTypeExists(objectApiName, {
    label: objectApiName.replace(/__c$/, '').replace(/_/g, ' '),
    category: 'custom',
    isCustom: true,
    isActive: true,
  });

  const response = await retryWithBackoff(
    async () => {
      const payload = await convertToGobodoRecordPayload(objectApiName, recordData);
      console.log('Converted payload for Gobodo:', JSON.stringify(payload, null, 2));
      const client = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(client);
      return objectsApi.createObject(payload);
    },
    { maxRetries: 3, initialDelayMs: 1500 }
  );

  console.log('Raw Gobodo response:', response);
  const converted = convertFromGobodoRecord(response, objectApiName);
  console.log('Converted response:', converted);
  console.log('  - converted.id:', converted.id);
  console.log('  - converted.Account__c:', (converted as any).Account__c);
  console.log('  - converted.custom__account:', (converted as any).custom__account);
  console.groupEnd();

  return converted;
}

/** Default chunk size for {@link createGobodoRecords} (POST /object/batch). */
export const DEFAULT_GOBODO_CREATE_RECORDS_CHUNK_SIZE = 5;

/** Max concurrent {@link createGobodoRecords} chunk uploads (waves of createObjects). */
export const MAX_GOBODO_CREATE_RECORDS_CHUNK_CONCURRENCY = 8;

export interface CreateGobodoRecordsOptions {
  chunkSize?: number;
  /** Max parallel chunk requests; 1 = strictly sequential (default). Clamped 1–{@link MAX_GOBODO_CREATE_RECORDS_CHUNK_CONCURRENCY}. */
  chunkConcurrency?: number;
  /** Called after each successful chunk (converted records). With concurrency 1, runs before the next upload starts. With concurrency > 1, runs as soon as each upload finishes; callbacks are queued so progress counts stay correct. */
  onChunkComplete?: (records: GenericRecord[]) => void | Promise<void>;
  /** Called after each chunk with cumulative rows created vs total input rows. */
  onBatchProgress?: (completed: number, total: number) => void;
}

/**
 * Create multiple record instances in Gobodo via GraphObjectsApi.createObjects (POST /object/batch).
 * Ensures the object type once, then sends chunked batch requests (atomicity is per chunk only).
 */
export async function createGobodoRecords(
  objectApiName: string,
  recordDataList: Record<string, any>[],
  options?: CreateGobodoRecordsOptions
): Promise<GenericRecord[]> {
  if (recordDataList.length === 0) {
    return [];
  }

  const normalizedApiName = normalizeToGobodoFormat(objectApiName);
  const chunkSize = Math.max(1, options?.chunkSize ?? DEFAULT_GOBODO_CREATE_RECORDS_CHUNK_SIZE);
  const rawConcurrency = options?.chunkConcurrency ?? 1;
  const chunkConcurrency = Math.min(
    MAX_GOBODO_CREATE_RECORDS_CHUNK_CONCURRENCY,
    Math.max(1, Math.floor(rawConcurrency))
  );

  const typeId = await ensureObjectTypeExists(normalizedApiName, {
    label: normalizedApiName.replace(/__c$/, '').replace(/_/g, ' '),
    category: 'custom',
    isCustom: true,
    isActive: true,
  });

  const fieldDefinitionsForBatch = await getAllGobodoFields(normalizedApiName, { objectTypeId: typeId });

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);
  const totalRows = recordDataList.length;

  const chunks: Record<string, any>[][] = [];
  for (let i = 0; i < recordDataList.length; i += chunkSize) {
    chunks.push(recordDataList.slice(i, i + chunkSize));
  }

  let completedRows = 0;
  const emitProgress = async (converted: GenericRecord[]) => {
    completedRows += converted.length;
    options?.onBatchProgress?.(completedRows, totalRows);
    if (options?.onChunkComplete) {
      await options.onChunkComplete(converted);
    }
  };

  const uploadChunk = async (chunk: Record<string, any>[]): Promise<GenericRecord[]> => {
    const payloads = await Promise.all(
      chunk.map((row) => convertToGobodoRecordPayload(normalizedApiName, row, typeId, fieldDefinitionsForBatch))
    );
    const response = await retryWithBackoff(
      () => objectsApi.createObjects(payloads, {}),
      { maxRetries: 3, initialDelayMs: 1500 }
    );
    if (!Array.isArray(response)) {
      throw new Error(
        `[createGobodoRecords] Expected array from createObjects for ${normalizedApiName}, got ${typeof response}`
      );
    }
    if (response.length !== payloads.length) {
      throw new Error(
        `[createGobodoRecords] createObjects returned ${response.length} items, expected ${payloads.length} for ${normalizedApiName}`
      );
    }
    return response.map((item) => convertFromGobodoRecord(item, normalizedApiName));
  };

  options?.onBatchProgress?.(0, totalRows);

  const all: GenericRecord[] = [];

  if (chunkConcurrency === 1) {
    for (const chunk of chunks) {
      const converted = await uploadChunk(chunk);
      all.push(...converted);
      await emitProgress(converted);
    }
  } else {
    // Serialize progress/UI callbacks so completedRows stays correct; do not wait for the full wave before emitting.
    let emitTail: Promise<void> = Promise.resolve();
    const scheduleEmit = (converted: GenericRecord[]): Promise<void> => {
      const next = emitTail.then(() => emitProgress(converted));
      emitTail = next.catch(() => {
        /* keep queue alive if a callback throws */
      });
      return next;
    };

    for (let w = 0; w < chunks.length; w += chunkConcurrency) {
      const wave = chunks.slice(w, w + chunkConcurrency);
      const waveResults = await Promise.all(
        wave.map(async (ch) => {
          const converted = await uploadChunk(ch);
          await scheduleEmit(converted);
          return converted;
        })
      );
      for (const converted of waveResults) {
        all.push(...converted);
      }
    }
  }

  return all;
}

export interface GobodoRecordsResult {
  records: GenericRecord[];
  total?: number;
}

/**
 * Get records for an object type from Gobodo (paginated).
 * When the API returns totalElements/total in the response, includes it so callers avoid a separate count request.
 * @param objectApiName - Object type API name (e.g., "Product__c")
 * @param options - Optional pagination and server-side cache options
 * @returns Records and optional total from paginated response
 */
export async function getGobodoRecords(
  objectApiName: string,
  options?: { page?: number; size?: number; cache?: GobodoCacheOpts }
): Promise<GobodoRecordsResult> {
  // Normalize object API name to Gobodo format (e.g., 'Client' -> 'Client__c')
  const normalizedApiName = normalizeToGobodoFormat(objectApiName);
  
  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);
  
  const graphId = GobodoConfig.graph();
  const page = options?.page ?? 0;
  const size = options?.size ?? 50;
  const opts: Record<string, unknown> = { page, size };
  if (options?.cache) {
    opts.cachekey = options.cache.cachekey;
    if (options.cache.cacheduration != null) opts.cacheduration = options.cache.cacheduration;
    if (options.cache.cachegroup != null) opts.cachegroup = options.cache.cachegroup;
    if (options.cache.cachescope != null) opts.cachescope = options.cache.cachescope;
  }
  
  if (GOBODO_RECORD_DEBUG) console.log(`[getGobodoRecords] Fetching ${normalizedApiName}, graph ${graphId}`);
  let gobodoRecords: any;
  try {
    // Reduced retries to avoid blocking concurrency limiter on persistent failures
    gobodoRecords = await retryWithBackoff(
      () => objectsApi.getObjectsInGraphByType(graphId, normalizedApiName, opts),
      { maxRetries: 2, initialDelayMs: 1000 }
    );
  } catch (error: any) {
    const status = error?.status ?? error?.statusCode ?? error?.response?.status ?? error?.response?.statusCode;
    const msg = String(error?.message ?? '');
    const isAborted = msg.includes('terminated') || msg.includes('aborted') || error?.name === 'AbortError';
    if (status === 404 || status === 500 || status === 504) {
      if (status === 504) console.warn(`[getGobodoRecords] Gateway timeout (504) for ${normalizedApiName}, returning []`);
      else console.warn(`[getGobodoRecords] Object type ${normalizedApiName} not found or unavailable (${status}), returning []`);
      return { records: [] };
    }
    if (!isAborted) console.warn(`[getGobodoRecords] Error for ${objectApiName}:`, status || msg || error);
    throw ensureError(error);
  }
  
  if (!gobodoRecords) {
    if (GOBODO_RECORD_DEBUG) console.log(`[getGobodoRecords] No records found for ${objectApiName} (null/undefined response)`);
    return { records: [] };
  }
  let recordsArray: unknown[];
  let total: number | undefined;
  if (Array.isArray(gobodoRecords)) {
    recordsArray = gobodoRecords;
  } else {
    const r = gobodoRecords as Record<string, unknown>;
    recordsArray = (Array.isArray(r?.content) ? r.content : Array.isArray(r?.items) ? r.items : Array.isArray(r?.data) ? r.data : []) as unknown[];
    if (typeof r?.totalElements === 'number') total = r.totalElements;
    else if (typeof r?.total === 'number') total = r.total;
    else if (typeof r?.count === 'number') total = r.count;
    if (recordsArray.length === 0 && r && Object.keys(r).length > 0 && process.env.NODE_ENV === 'development') {
      console.warn('[getGobodoRecords] API returned non-array for', normalizedApiName, '- keys:', Object.keys(r).slice(0, 10));
    }
  }
  if (recordsArray.length === 0) {
    if (GOBODO_RECORD_DEBUG) console.log(`[getGobodoRecords] No records found for ${objectApiName} (empty array)`);
    return { records: [], total };
  }
  const convertedRecords = recordsArray.map((record: any) => {
    try {
      const objectTypeKey = record.objecttypekey || record.objecttype?.key || normalizedApiName;
      const converted = convertFromGobodoRecord(record, objectTypeKey);
      if (GOBODO_RECORD_DEBUG) console.log(`[getGobodoRecords] Converted record:`, { gobodo: record, converted });
      return converted;
    } catch (error: any) {
      console.error(`[getGobodoRecords] Error converting record:`, error, record);
      // Return a minimal record to prevent breaking the entire list
      return {
        id: String(record.id || Date.now()),
        ...(record.data || record.properties || {}),
        label: record.label || 'Unknown',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString()
      };
    }
  });
  if (GOBODO_RECORD_DEBUG) console.log(`[getGobodoRecords] Successfully converted ${convertedRecords.length} records`);
  return { records: convertedRecords, total };
}

/** Cache opts for count endpoint so gobodo-api-promise can cache (feeds pagination total). */
const RECORD_COUNT_CACHE_OPTS = {
  cachekey: ['gobodo-record-count'],
  cacheduration: '60',
};

/**
 * Get count of records for an object type in the graph (for pagination).
 * Uses GraphsApi.getObjectCountByTypeInGraph() for a dedicated count; falls back to
 * getObjectsInGraphByType when the count endpoint is unavailable.
 * @param objectApiName - Object type API name (e.g., "Account", "Account__c")
 * @returns Total count of objects of this type in the graph
 */
export async function getGobodoRecordCount(objectApiName: string): Promise<number> {
  const normalizedApiName = normalizeToGobodoFormat(objectApiName);
  const client = getGobodoClient();
  const graphId = GobodoConfig.graph();

  if (GOBODO_RECORD_DEBUG || process.env.NODE_ENV === 'development') {
    console.log(`[getGobodoRecordCount] Calling getObjectCountByTypeInGraph for ${normalizedApiName}, graph ${graphId}`);
  }

  try {
    const graphsApi = new GobodoApi.GraphsApi(client);
    const response = await retryWithBackoff(
      () =>
        graphsApi.getObjectCountByTypeInGraph(graphId, normalizedApiName, {
          cachekey: [...RECORD_COUNT_CACHE_OPTS.cachekey, normalizedApiName],
          cacheduration: RECORD_COUNT_CACHE_OPTS.cacheduration,
        }),
      { maxRetries: 2, initialDelayMs: 1000 }
    );
    const raw =
      typeof response === 'number'
        ? response
        : response && typeof (response as { count?: number }).count === 'number'
          ? (response as { count: number }).count
          : typeof (response as { getCount?: () => number })?.getCount === 'function'
            ? (response as { getCount: () => number }).getCount()
            : undefined;
    const count = raw != null && !Number.isNaN(Number(raw)) ? Number(raw) : undefined;
    if (count != null && !Number.isNaN(count)) return Math.max(0, Math.floor(count));
    return 0;
  } catch (error: any) {
    const status = error?.status ?? error?.statusCode ?? error?.response?.status ?? error?.response?.statusCode;
    const msg = error?.message ?? '';
    if (status === 404 || status === 500 || status === 504) {
      if (status === 504) console.warn(`[getGobodoRecordCount] Gateway timeout (504) for ${normalizedApiName}, returning 0`);
      else console.warn(`[getGobodoRecordCount] getObjectCountByTypeInGraph unavailable (${status}), returning 0`);
      return 0;
    }
    console.warn(`[getGobodoRecordCount] Error for ${objectApiName}:`, status || msg || error);
    throw ensureError(error);
  }
}

/**
 * Get a single record by ID from Gobodo
 * @param recordId - Record ID (Gobodo numeric ID as string)
 * @param cacheOpts - Optional server cache bag for `GET /object/{id}`. Use {@link makeObjectByKeyReadCacheOpts}
 *   with the record’s object `key` so `cachekey` matches read-by-key / property PUT (see `getRecordById` options).
 * @returns Record in GenericRecord format, or null if not found
 */
export async function getGobodoRecordById(
  recordId: string,
  cacheOpts?: GobodoCacheOpts
): Promise<GenericRecord | null> {
  try {
    const client = getGobodoClient();
    const objectsApi = new GobodoApi.GraphObjectsApi(client);
    const gobodoId = extractGobodoId(recordId);
    if (isNaN(gobodoId)) {
      if (GOBODO_RECORD_DEBUG) console.warn(`[getGobodoRecordById] Invalid record ID format: ${recordId}`);
      return null;
    }
    const opts = cacheOpts ?? makeObjectByIdReadCacheOpts(gobodoId, GobodoConfig.graph());
    const gobodoRecord = await retryWithBackoff(
      () => objectsApi.getObjectById(gobodoId, opts),
      { maxRetries: 3 }
    );

    const objectTypeKey = gobodoRecord.objecttypekey || gobodoRecord.objecttype?.key || '';
    return convertFromGobodoRecord(gobodoRecord, objectTypeKey);
  } catch (error: any) {
    const status = error?.status ?? error?.response?.status;
    if (status === 404) return null;
    console.error(`Failed to get Gobodo record ${recordId}:`, error);
    throw ensureError(error);
  }
}

/**
 * Get a single record by key from Gobodo
 * @param key - Record key
 * @returns Record in GenericRecord format, or null if not found
 */
export async function getGobodoRecordByKey(key: string, cacheOpts?: GobodoCacheOpts): Promise<GenericRecord | null> {
  const normalizedKey = ensureGobodoCustomSuffixOnRecordKey(key);
  try {
    const client = getGobodoClient();
    const objectsApi = new GobodoApi.GraphObjectsApi(client);

    const opts = cacheOpts ?? makeObjectByKeyReadCacheOpts(normalizedKey, GobodoConfig.graph());
    const gobodoRecord = await retryWithBackoff(
      () => objectsApi.getObjectByKey(normalizedKey, opts),
      { maxRetries: 3 }
    );

    // Extract object type key from response
    const objectTypeKey = gobodoRecord.objecttypekey || gobodoRecord.objecttype?.key || '';

    return convertFromGobodoRecord(gobodoRecord, objectTypeKey);
  } catch (error: any) {
    const status = error?.status ?? error?.response?.status;
    if (status === 404) {
      return null;
    }
    console.error(`Failed to get Gobodo record by key ${normalizedKey}:`, error);
    throw ensureError(error);
  }
}

/**
 * Update an existing record in Gobodo
 * @param recordId - Record ID (Gobodo numeric ID as string)
 * @param updates - Field updates to apply
 * @param options - `propertyPatch` sends only `updates` keys via setObjectPropertiesById / setObjectPropertiesByKey; default merges and uses updateObject
 * @returns Updated record in GenericRecord format
 * @throws Error if record ID is invalid or update fails
 */
export async function updateGobodoRecord(
  recordId: string,
  updates: Record<string, any>,
  options?: UpdateGobodoRecordOptions
): Promise<GenericRecord> {
  const mode = options?.gobodoUpdateMode ?? 'fullObject';
  return retryWithBackoff(
    async () => {
      const client = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(client);

      // Support both numeric Gobodo IDs and string keys
      const gobodoId = extractGobodoId(recordId);
      const fetchedByNumericId = !isNaN(gobodoId) && String(gobodoId) === recordId;
      const keyForGobodo = fetchedByNumericId ? null : ensureGobodoCustomSuffixOnRecordKey(recordId);
      const existingRecord = fetchedByNumericId
        ? await objectsApi.getObjectById(gobodoId)
        : await objectsApi.getObjectByKey(keyForGobodo!);
      const objectTypeKey = existingRecord.objecttypekey || existingRecord.objecttype?.key || '';

      let resolvedTypeId = existingRecord.objecttype?.id;
      if (!resolvedTypeId && objectTypeKey) {
        const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
        const objectType = await retryWithBackoff(
          () => objectTypesApi.getObjectTypeByKey(objectTypeKey, { maxFields: 0 }),
          { maxRetries: 2 }
        );
        resolvedTypeId = objectType.id;
      }
      if (!resolvedTypeId && !objectTypeKey) {
        throw new Error(`Cannot update record ${recordId}: unable to resolve object type`);
      }

      let fieldTypeMap: Record<string, string> = {};
      try {
        const fields = await getAllGobodoFields(objectTypeKey, {
          objectTypeId: resolvedTypeId != null ? Number(resolvedTypeId) : undefined,
        });
        fields.forEach(f => { fieldTypeMap[f.fieldName] = f.fieldType; });
      } catch { /* fields unavailable -- treat all as text */ }

      const numericId = typeof existingRecord.id === 'number' ? existingRecord.id : parseInt(String(existingRecord.id), 10);

      if (mode === 'propertyPatch') {
        const patchBody: Record<string, any> = {};
        for (const [key, value] of Object.entries(updates)) {
          const coerced = coerceGobodoPropertyForPayload(key, value, fieldTypeMap);
          if (coerced === undefined) continue;
          patchBody[key] = coerced;
        }

        const graphId = GobodoConfig.graph();
        const existingKey = (existingRecord as { key?: string }).key;
        const gobodoKey =
          existingKey != null && String(existingKey).trim()
            ? ensureGobodoCustomSuffixOnRecordKey(String(existingKey))
            : null;

        if (Object.keys(patchBody).length > 0) {
          // When the record has a Gobodo key, use by-key PUT + by-key read so server cache lines match object key.
          const propertyWriteCacheOpts = gobodoKey
            ? makeObjectPropertyWriteCacheOpts({ path: 'byKey', recordKey: gobodoKey, graphId })
            : makeObjectPropertyWriteCacheOpts({ path: 'byId', numericId, graphId });
          const patchTarget = gobodoKey ? `key:${gobodoKey}` : `id:${numericId}`;
          console.log('[updateGobodoRecord] propertyPatch setObjectPropertiesBy' + (gobodoKey ? 'Key' : 'Id') + ':', patchTarget, objectTypeKey);
          console.log('  - Properties:', JSON.stringify(patchBody, null, 2));
          if (gobodoKey) {
            await objectsApi.setObjectPropertiesByKey(gobodoKey, patchBody, propertyWriteCacheOpts);
          } else {
            await objectsApi.setObjectPropertiesById(numericId, patchBody, propertyWriteCacheOpts);
          }
        } else {
          console.log('[updateGobodoRecord] propertyPatch: no properties after coercion; skipping PUT');
        }

        if (gobodoKey) {
          const refreshed = await objectsApi.getObjectByKey(
            gobodoKey,
            makeObjectByKeyReadCacheOpts(gobodoKey, graphId)
          );
          return convertFromGobodoRecord(refreshed, objectTypeKey);
        }
        const refreshed = await objectsApi.getObjectById(
          numericId,
          makeObjectByIdReadCacheOpts(numericId, graphId)
        );
        return convertFromGobodoRecord(refreshed, objectTypeKey);
      }

      const existingProperties = existingRecord.data || existingRecord.properties || {};
      const merged = { ...existingProperties, ...updates };
      const cleanProperties: Record<string, any> = {};

      for (const [key, value] of Object.entries(merged)) {
        const coerced = coerceGobodoPropertyForPayload(key, value, fieldTypeMap);
        if (coerced === undefined) continue;
        cleanProperties[key] = coerced;
      }

      const stripLegacyMirroredPropertyKeys = (props: Record<string, any>) => {
        for (const k of Object.keys(props)) {
          if (/_c__c$/i.test(k)) delete props[k];
        }
        if (props.Sub_Type__c != null && 'SubType__c' in props) delete props.SubType__c;
        if (props.record_type != null && 'Record_type__c' in props) delete props.Record_type__c;
      };
      stripLegacyMirroredPropertyKeys(cleanProperties);

      const payload = new GobodoApi.GraphObjectPayload();
      payload.typekey = objectTypeKey;
      payload.typeid = resolvedTypeId as any;
      payload.label = extractRecordLabel(merged) || existingRecord.label;
      payload.graphs = [GobodoConfig.graph()];
      payload.properties = cleanProperties;

      console.log('[updateGobodoRecord] fullObject update to Gobodo API:');
      console.log('  - Record ID:', numericId);
      console.log('  - Object Type:', objectTypeKey);
      console.log('  - Properties being sent:', JSON.stringify(cleanProperties, null, 2));

      const updatedRecord = await objectsApi.updateObject(numericId, payload);

      console.log('[updateGobodoRecord] Gobodo API response:', updatedRecord);
      console.log('[updateGobodoRecord] Response properties:', updatedRecord?.data || updatedRecord?.properties);

      return convertFromGobodoRecord(updatedRecord, objectTypeKey);
    },
    { maxRetries: 3, initialDelayMs: 1500 }
  );
}

/**
 * Delete a record from Gobodo
 * @param recordId - Record ID (Gobodo numeric ID as string)
 * @returns true if deleted successfully
 * @throws Error if record ID is invalid or delete fails
 */
export async function deleteGobodoRecord(recordId: string): Promise<boolean> {
  const gobodoId = extractGobodoId(recordId);
  if (isNaN(gobodoId)) {
    throw new Error(`Invalid record ID format for delete: ${recordId}`);
  }

  await retryWithBackoff(
    async () => {
      const client = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(client);
      await objectsApi.deleteObject(gobodoId);
    },
    { maxRetries: 3, initialDelayMs: 1500 }
  );
  return true;
}

// ============================================
// SEARCH OPERATIONS
// ============================================

export interface SearchGraphObjectsOpts {
  graphid: number;
  max?: number;
  objecttypekey?: string;
  reltypeid?: number;
  /** Gobodo server-side cache (seconds). */
  cacheduration?: string;
  cachekey?: string[];
  cachegroup?: string;
  cachescope?: string;
}

/**
 * Call Gobodo searchGraphObjects API and return results as GenericRecord[].
 * Used by lookup/master-detail autocomplete for server-side search.
 */
export async function searchGraphObjectsAsRecords(
  query: string,
  opts: SearchGraphObjectsOpts
): Promise<GenericRecord[]> {
  if (!USE_GOBODO_API) {
    return [];
  }

  try {
    const client = getGobodoClient();
    const objectsApi = new GobodoApi.GraphObjectsApi(client);
    const graphId = opts.graphid;
    const max = opts.max ?? 50;

    const response = await retryWithBackoff(
      () =>
        objectsApi.searchGraphObjects(query, {
          graphid: graphId,
          max,
          objecttypekey: opts.objecttypekey,
          reltypeid: opts.reltypeid,
          ...(opts.cacheduration != null ? { cacheduration: opts.cacheduration } : {}),
          ...(opts.cachekey != null && opts.cachekey.length > 0 ? { cachekey: opts.cachekey } : {}),
          ...(opts.cachegroup != null && opts.cachegroup.length > 0 ? { cachegroup: opts.cachegroup } : {}),
          ...(opts.cachescope != null ? { cachescope: opts.cachescope } : {}),
        }),
      { maxRetries: 2 }
    );

    const raw = Array.isArray(response) ? response : [];
    const records: GenericRecord[] = [];
    for (const item of raw) {
      try {
        const objectTypeKey =
          (item as any).objecttypekey ||
          (item as any).objecttype?.key ||
          opts.objecttypekey ||
          '';
        const converted = convertFromGobodoRecord(item, objectTypeKey);
        records.push(converted);
      } catch (err: any) {
        console.error('[searchGraphObjectsAsRecords] Error converting record:', err, item);
      }
    }
    return records;
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'searchGraphObjectsAsRecords', query });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw ensureError(error);
  }
}

/**
 * Search for records by object type. Uses getObjectsInGraphByType + client-side filter
 * (SearchApi is not in current @gobodo/gobodo-api-promise). objectTypeKey required.
 */
export async function searchGobodoRecords(
  query: string,
  objectTypeKey?: string,
  options?: { start?: number; max?: number; graphid?: number }
): Promise<GenericRecord[]> {
  if (!USE_GOBODO_API) {
    return [];
  }
  if (!objectTypeKey) {
    return [];
  }

  try {
    const max = options?.max ?? 50;
    const { records } = await getGobodoRecords(objectTypeKey, { page: 0, size: Math.max(max, 200) });
    const q = (query || '').trim().toLowerCase();
    if (!q) {
      return records.slice(0, max);
    }
    const filtered = records.filter((r: GenericRecord) => {
      const label = (r.label ?? r.name ?? (r as any).Name__c ?? '').toString().toLowerCase();
      return label.includes(q);
    });
    return filtered.slice(0, max);
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'searchGobodoRecords', query });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw ensureError(error);
  }
}

/**
 * Get end object types from a relationship type
 * @param relTypeId - Relationship type ID
 * @returns Array of object type keys (strings)
 */
export async function getEndObjectTypesFromRelationshipType(relTypeId: number): Promise<string[]> {
  if (!USE_GOBODO_API) {
    console.warn('[getEndObjectTypesFromRelationshipType] USE_GOBODO_API is false, returning empty array');
    return [];
  }

  try {
    const client = getGobodoClient();
    const relationshipTypesApi = new GobodoApi.GraphRelationshipTypesApi(client);

    if (GOBODO_RECORD_DEBUG) console.log(`[getEndObjectTypesFromRelationshipType] Fetching relationship type ${relTypeId}`);
    const relType = await retryWithBackoff(
      () => relationshipTypesApi.getRelationshipTypeById(relTypeId),
      { maxRetries: 2 }
    );
    if (GOBODO_RECORD_DEBUG) console.log(`[getEndObjectTypesFromRelationshipType] Relationship type response:`, relType);
    const endObjectTypes = relType.endObjectTypes || [];
    const objectTypeKeys = endObjectTypes
      .map((ot: any) => ot.key || ot.name)
      .filter((key: string | undefined): key is string => !!key);
    if (GOBODO_RECORD_DEBUG) console.log(`[getEndObjectTypesFromRelationshipType] Extracted object type keys:`, objectTypeKeys);

    return objectTypeKeys;
  } catch (error: any) {
    console.error(`[getEndObjectTypesFromRelationshipType] Failed to get relationship type ${relTypeId}:`, error);
    const gobodoError = handleGobodoError(error, { operation: 'getEndObjectTypesFromRelationshipType', relTypeId });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw ensureError(error);
  }
}

/**
 * Search records for a lookup field using searchGraphObjects API.
 * Resolves end object types from the relationship type and feeds them into the search.
 * Empty query falls back to getGobodoRecords so "show all" still works.
 */
/**
 * Defensive client-side filtering for lookup search results. Gobodo's searchGraphObjects often
 * returns broad/unfiltered results (no Name__c index, multi-word queries dropped, etc.), so we
 * post-filter:
 *   1. record's display label must contain every space-separated token of the query (case-insensitive)
 *   2. drop Person Accounts when looking up Partner Accounts (lookupObject: 'Account' / 'PartnerAccount')
 *
 * Empty query short-circuits — caller already handled the empty-query "show all" case.
 */
export function applyLookupResultFilters(
  records: GenericRecord[],
  query: string,
  lookupObject?: string
): GenericRecord[] {
  const q = (query || '').trim().toLowerCase();
  const tokens = q ? q.split(/\s+/).filter(Boolean) : [];

  const lookupKey = String(lookupObject ?? '').trim().toLowerCase().replace(/__\w*c$/, '');
  // Account Manager Company → business / partner accounts only (exclude Person Accounts)
  const wantsPartnerAccounts =
    lookupKey === 'account' || lookupKey === 'partner' || lookupKey === 'partneraccount';
  // Spouse / Primary Parent → individual clients only (exclude business accounts like "Regal Credit Management")
  const wantsPersonAccounts = lookupKey === 'client';
  const wantsAccountOrClient = wantsPartnerAccounts || wantsPersonAccounts;

  const recordLabel = (r: GenericRecord): string => {
    const candidates = [
      r.label,
      r.Name__c,
      (r as Record<string, unknown>).name,
      (r as Record<string, unknown>).ClientName__c,
    ];
    for (const c of candidates) {
      const s = typeof c === 'string' ? c.trim() : '';
      if (s) return s.toLowerCase();
    }
    return '';
  };

  const isPersonAccount = (r: GenericRecord): boolean => {
    const data = (r as Record<string, unknown>);
    const flag = data.IsPersonAccount__c ?? data.isPersonAccount ?? data.IsPersonAccount;
    if (flag === true || flag === 'true') return true;
    return false;
  };

  const isExplicitlyBusinessAccount = (r: GenericRecord): boolean => {
    const data = (r as Record<string, unknown>);
    const flag = data.IsPersonAccount__c ?? data.isPersonAccount ?? data.IsPersonAccount;
    return flag === false || flag === 'false';
  };

  const isAccountOrClientType = (r: GenericRecord): boolean => {
    const typeKey = String(
      (r as Record<string, unknown>).objecttypekey ??
      (r as Record<string, unknown>).objectTypeKey ??
      ''
    ).toLowerCase();
    if (!typeKey) return true; // unknown type — don't filter aggressively
    return typeKey.startsWith('account') || typeKey.startsWith('client');
  };

  return records.filter((r) => {
    // When looking up Accounts/Clients, exclude non-Account types (e.g. User records that the
    // server-side search sometimes returns when a relationship's end-type isn't strictly enforced).
    if (wantsAccountOrClient && !isAccountOrClientType(r)) return false;
    // Account Manager Company / Partner lookups: Person Accounts shouldn't appear.
    if (wantsPartnerAccounts && isPersonAccount(r)) return false;
    // Spouse / Primary Parent (Client lookups): business accounts (e.g. Regal Credit Management)
    // shouldn't appear. We only filter when IsPersonAccount__c is explicitly false — records
    // missing that flag are treated as Person Accounts to avoid over-filtering legacy data.
    if (wantsPersonAccounts && isExplicitlyBusinessAccount(r)) return false;
    if (tokens.length === 0) return true;
    const label = recordLabel(r);
    if (!label) return false;
    return tokens.every((tok) => label.includes(tok));
  });
}

export async function searchRecordsForLookupField(
  field: CustomField,
  searchQuery: string
): Promise<GenericRecord[]> {
  if (!USE_GOBODO_API) {
    console.warn('[searchRecordsForLookupField] USE_GOBODO_API is false, returning empty array');
    return [];
  }

  const graphId = GobodoConfig.graph();
  const q = (searchQuery || '').trim();

  try {
    let targetObjectTypeKeys: string[] = [];

    if (field.relTypeId) {
      if (GOBODO_RECORD_DEBUG) console.log(`[searchRecordsForLookupField] Using relTypeId ${field.relTypeId} for field ${field.fieldName}`);
      targetObjectTypeKeys = await getEndObjectTypesFromRelationshipType(field.relTypeId);
    } else if (field.lookupObject) {
      if (GOBODO_RECORD_DEBUG) console.log(`[searchRecordsForLookupField] Using lookupObject ${field.lookupObject} for field ${field.fieldName}`);
      targetObjectTypeKeys = [field.lookupObject];
    } else {
      console.warn(`[searchRecordsForLookupField] Field ${field.fieldName} has neither relTypeId nor lookupObject`);
      return [];
    }

    if (targetObjectTypeKeys.length === 0) {
      console.warn(`[searchRecordsForLookupField] No target object types found for field ${field.fieldName}`);
      return [];
    }

    // Empty query: fallback to getGobodoRecords so opening dropdown still shows records
    if (!q) {
      const allResults: GenericRecord[] = [];
      for (const key of targetObjectTypeKeys) {
        try {
          const { records } = await getGobodoRecords(key, { page: 0, size: 50 });
          allResults.push(...records);
        } catch (error: any) {
          console.error(`[searchRecordsForLookupField] Fallback failed for ${key}:`, error);
        }
      }
      if (GOBODO_RECORD_DEBUG) console.log(`[searchRecordsForLookupField] Empty query fallback: ${allResults.length} results`);
      return allResults;
    }

    // Non-empty query: use searchGraphObjects API
    if (field.relTypeId) {
      const results = await searchGraphObjectsAsRecords(q, {
        graphid: graphId,
        max: 50,
        reltypeid: field.relTypeId,
      });
      if (GOBODO_RECORD_DEBUG) console.log(`[searchRecordsForLookupField] searchGraphObjects reltypeid: ${results.length} raw results`);
      return applyLookupResultFilters(results, q, field.lookupObject);
    }

    // lookupObject only (single or multiple keys): one call per objecttypekey, merge
    const allResults: GenericRecord[] = [];
    const seenIds = new Set<string>();
    for (const key of targetObjectTypeKeys) {
      try {
        const normalizedKey = normalizeToGobodoFormat(key);
        const results = await searchGraphObjectsAsRecords(q, {
          graphid: graphId,
          max: 50,
          objecttypekey: normalizedKey,
        });
        for (const r of results) {
          if (!seenIds.has(r.id)) {
            seenIds.add(r.id);
            allResults.push(r);
          }
        }
      } catch (error: any) {
        console.error(`[searchRecordsForLookupField] Failed to search ${key}:`, error);
      }
    }
    if (GOBODO_RECORD_DEBUG) console.log(`[searchRecordsForLookupField] Total raw results: ${allResults.length}`);
    return applyLookupResultFilters(allResults, q, field.lookupObject);
  } catch (error: any) {
    console.error(`[searchRecordsForLookupField] Failed to search records for lookup field:`, error);
    throw ensureError(error);
  }
}
